import { Route, Routes, useNavigate, useParams } from "react-router-dom";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";

import { showCreateTemplateDrawer } from "../TemplatesWorkbench/CreateTemplateDrawer";
import { FILTERS_ORDER_SETTINGS_KEY } from "../TemplatesWorkbench/constants";
import { showDeleteConfirmation } from "../TemplatesWorkbench/DeleteConfirmation";
import { showTemplateDetailsDrawer } from "./DetailsDrawer";
import useTemplate from "./useTemplate";
import TemplateVersions from "./Versions";
import { TemplateContext } from "./Context";
import { REFETCH_QUERIES_LIST } from "./Versions/constants";
import TemplateDeployments from "./Deployments";
import PageLayout from "./PageLayout";
import TemplateDeploymentPage from "../TemplateDeployment";
import TemplateDeploymentOutputs from "../TemplateDeployment/Outputs";
import TemplateDeploymentStacks from "../TemplateDeployment/Stacks";

const TemplatePage = () => {
  const navigate = useNavigate();

  const { templateId } = useParams<{ templateId: string }>();

  const { error, loading, data } = useTemplate(templateId);

  const handleOpenEditDrawer = () => {
    showCreateTemplateDrawer({
      template: data?.blueprintVersionedGroup,
      refetchQueries: REFETCH_QUERIES_LIST,
    });
  };

  const handleOpenDetailsDrawer = () => {
    if (data?.blueprintVersionedGroup) {
      showTemplateDetailsDrawer({
        item: data.blueprintVersionedGroup,
      });
    }
  };

  useBreadcrumbs(
    [
      {
        title: "Templates Workbench",
        link: getBreadcrumbsBackUrl("/templates-workbench", FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: data?.blueprintVersionedGroup?.name || "",
      },
    ],
    [data?.blueprintVersionedGroup?.name]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.blueprintVersionedGroup) {
    return <PageLoading />;
  }

  if (!data?.blueprintVersionedGroup) {
    return <NotFoundPage />;
  }

  const template = data.blueprintVersionedGroup;

  const handleDeleteClick = () => {
    showDeleteConfirmation({ name: template.name, id: template.id }).then(() =>
      navigate("/templates-workbench")
    );
  };

  const templateUrl = `/template/${template.id}`;

  return (
    <TemplateContext.Provider value={{ template, templateUrl }}>
      <Routes>
        <Route path="deployment/:deploymentId" element={<TemplateDeploymentPage />}>
          <Route index element={<TemplateDeploymentStacks />} />
          <Route path="outputs" element={<TemplateDeploymentOutputs />} />
        </Route>

        <Route
          path="*"
          element={
            <PageLayout
              onOpenDetailsDrawer={handleOpenDetailsDrawer}
              onOpenEditDrawer={handleOpenEditDrawer}
              onDeleteClick={handleDeleteClick}
            />
          }
        >
          <Route index element={<TemplateVersions id={data.blueprintVersionedGroup.id} />} />
          <Route
            path="deployments"
            element={<TemplateDeployments id={data.blueprintVersionedGroup.id} />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </TemplateContext.Provider>
  );
};

export default TemplatePage;
